import { useMemo, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { FORMULA_FUNCTIONS_URL, FunctionDescription } from "@/types/formula";
import { functionSignature, groupFunctions } from "@/utils/formula";
import { useFormulaFunctions } from "@/hooks/use-formula-catalog";

/**
 * The vocabulary an expression may call, with the signature and the sentence the engine
 * wrote for each.
 *
 * Nothing about a function is spelled here. The list, the parameter types, the arity, the
 * summaries and the headings all come from `GET /dialer/functions`, which is
 * `formula.functions.describe_functions()` passed through: the vocabulary is a dictionary a
 * host may add to, so a palette holding a second copy of it would be wrong the first time
 * somebody registers one.
 *
 * That is also why an unreachable referential says so instead of falling back on a
 * written-out list. The choice is between two ways of being unhelpful — a stale list that
 * looks authoritative, or a sentence admitting the list could not be read — and only the
 * second one is honest about which functions exist. It also costs the author nothing they
 * had: expressions are typed by hand and the live check below still names an unknown
 * function, so the palette is a convenience whose absence is survivable, while a list
 * teaching functions the engine no longer has is not.
 *
 * Collapsed by default: thirty functions above a field that mostly holds a path would push
 * the field itself out of a 400px column. `open` is for a caller that has the room — the
 * modal editor — because « what can I even write here » is exactly what this list answers.
 */
export function FunctionPalette({ url = FORMULA_FUNCTIONS_URL, open: initiallyOpen, onPick }: { url?: string; open?: boolean; onPick: (name: string) => void }) {
    const { options, loading } = useFormulaFunctions(url);
    const [open, setOpen] = useState(Boolean(initiallyOpen));
    const groups = useMemo(() => groupFunctions(options), [options]);

    return (
        <div className="rounded border border-gray-200" data-testid="function-palette">
            <button
                type="button"
                onClick={() => setOpen(value => !value)}
                aria-expanded={open}
                className="flex w-full items-center gap-1 px-2 py-1 text-left text-[11px] font-medium text-gray-600 hover:bg-gray-50"
            >
                {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                Functions
                {options.length > 0 && <span className="text-gray-400">({options.length})</span>}
            </button>

            {open && (
                <div className="max-h-56 overflow-y-auto border-t">
                    {loading && <p className="px-2 py-1.5 text-[11px] text-gray-500">Loading the vocabulary…</p>}
                    {!loading && options.length === 0 && (
                        <p className="px-2 py-1.5 text-[11px] leading-snug text-amber-800" data-testid="function-palette-empty">
                            The list of functions could not be read from the server, so this palette has nothing to list. Expressions still work, and the check below still names an unknown
                            function.
                        </p>
                    )}
                    {groups.map(group => (
                        <div key={group.group}>
                            {group.group && (
                                <p className="sticky top-0 bg-gray-50 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-gray-500">{group.group}</p>
                            )}
                            {group.items.map(description => (
                                <FunctionRow key={description.name} description={description} onPick={onPick} />
                            ))}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function FunctionRow({ description, onPick }: { description: FunctionDescription; onPick: (name: string) => void }) {
    return (
        <button
            type="button"
            data-testid={`function-${description.name}`}
            onMouseDown={event => {
                event.preventDefault();
                onPick(description.name);
            }}
            className="flex w-full flex-col items-start gap-0.5 px-2 py-1 text-left hover:bg-sky-50"
        >
            <span className="flex w-full items-baseline gap-2">
                <span className="min-w-0 flex-1 truncate font-mono text-[11px] text-gray-900">{functionSignature(description)}</span>
                <span className="shrink-0 rounded bg-gray-100 px-1 text-[10px] uppercase text-gray-600">{description.returns}</span>
            </span>
            <span className="text-[11px] leading-snug text-gray-500">{description.summary}</span>
        </button>
    );
}
